import { saveAs } from 'file-saver';
import _orderBy from 'lodash/orderBy';

import { baseApi, TAGS, type TagTypes } from 'shared/api';

import { type Document } from '../model';
import type { DocumentsResponse, DocumentsOptionsReponse } from './dto';
import {
  buildDeleteDocumentEndpoint,
  buildDownloadDocumentEndpoint,
  buildGetDocumentsEndpoint,
  buildUploadDocumentEndpoint,
  getFragmentByEntityType,
  getTagByEntityType,
} from './helpers';
import type {
  BaseRequestParameters,
  DeleteDocumentRequestParameters,
  DownloadDocumentRequestParameters,
  GetDocumentsRequestParameters,
  UploadDocumentsRequestParameters,
} from './request-params';

const sortDocuments = (documents: Document[]) =>
  _orderBy(documents, (document) => document.Name.toLocaleLowerCase(), 'asc');

export const fileLoaderApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getAttachments: build.query<Document[], GetDocumentsRequestParameters>({
      query: (args) => {
        console.log(args);
        return {
          url: buildGetDocumentsEndpoint(args),
          method: 'GET',
        };
      },
      providesTags: [TAGS.DOCUMENTS],
      transformResponse: (response: DocumentsResponse) =>
        sortDocuments(response.Data[0]?.Documents ?? []),
    }),
    getDocumentsOptions: build.query<
      { canCreateDocuments: boolean; isFolderExists: boolean },
      BaseRequestParameters
    >({
      query: (args) => ({
        url: `/user/cooperatives/${
          args.cooperativeId
        }/documents/${getFragmentByEntityType(args.entityType)}/${
          args.entityId
        }/options`,
      }),
      transformResponse: (response: DocumentsOptionsReponse) => ({
        canCreateDocuments: response.Data.CanCreateDocuments,
        isFolderExists: response.Data.IsFolderExist,
      }),
    }),
    deleteDocument: build.mutation<void, DeleteDocumentRequestParameters>({
      query: (args) => ({
        url: buildDeleteDocumentEndpoint(args),
        method: 'DELETE',
        params: {
          fileUrl: args.fileUrl,
        },
      }),
      invalidatesTags: (_res, error, args) => {
        if (error) return [];

        const tags: TagTypes = [TAGS.DOCUMENTS];
        const entityTag = getTagByEntityType(args.entityType);

        if (entityTag) tags.push(entityTag);

        return tags;
      },
    }),
    downloadDocument: build.query<null, DownloadDocumentRequestParameters>({
      queryFn: async (args, _api, _extraOptions, baseQuery) => {
        const res = await baseQuery({
          url: buildDownloadDocumentEndpoint(args),
          method: 'GET',
          params: {
            fileUrl: args.fileUrl,
          },
          responseHandler: (response) => response.blob(),
        });

        saveAs(res.data as Blob, args.fileName);

        return { data: null };
      },
    }),
    uploadDocument: build.mutation<void, UploadDocumentsRequestParameters>({
      query: (args) => ({
        url: buildUploadDocumentEndpoint(args),
        method: 'POST',
        body: args.formData,
      }),
      invalidatesTags: (_res, error, args) => {
        if (error) return [];

        const tags: TagTypes = [TAGS.DOCUMENTS];
        const entityTag = getTagByEntityType(args.entityType);

        if (entityTag) tags.push(entityTag);

        return tags;
      },
    }),
  }),
});
